import { Injectable } from '@angular/core';
import { interval, merge, Subject } from 'rxjs';
import {
  ImmoTaskChangeType,
  OfflineStorageTourService
} from 'src/app/pages/immo-ip/placer/tour-view/offline-storage/offline-storage-tour.service';
import { ConnectionStatusService } from '../connection-status.service';
import { ImmoIPImmoTaskChangesService } from '../immo-ip-placer-offline-changes/immo-ip-immo-task-changes/immo-ip-immo-task-changes.service';
import { ImmoIPImmoTaskSubmitInProgressService } from './immo-ip-immo-task-submit-in-progress.service';
import {
  ImmoIPImmoTaskSubmitQueueService,
  ImmoTaskSubmitType
} from './immo-ip-immo-task-submit-queue.service';
import { TourInstallerSubmitImmoTaskControllerRequestService } from './controller-request-services/tour-installer-submit-immo-task-controller-request.service';
import {
  ImmoTaskAssetSubmitResult,
  TourInstallerSubmitImmoTaskAssetControllerRequestService
} from './controller-request-services/tour-installer-submit-immo-task-asset-controller-request.service';
import { TourInstallerDeleteImmoTaskAssetControllerRequestService } from './controller-request-services/tour-installer-delete-immo-task-asset-controller-request.service';
import { TourInstallerResetImmoTaskNotExecutedControllerRequestService } from './controller-request-services/tour-installer-reset-immo-task-not-executed-controller-request.service';
import { SnackService } from '../snack.service';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import ErrorHelper from 'src/app/helpers/error-helper';
import { ImmoIPImmoTaskChangesObject } from '../immo-ip-placer-offline-changes/immo-ip-immo-task-changes/immo-ip-immo-task-changes-object';
import Asset from 'src/app/model/asset';
import { ImmoIPPlacerImmoTaskFunctions } from 'src/app/pages/immo-ip/placer/tour-item-detail/immo-task/helper/immo-ip-placer-immo-task-functions';
import { GeneralHelper } from 'src/app/helpers/general-helper';

@Injectable({
  providedIn: 'root'
})
export class ImmoIPImmoTaskSubmitService {
  private isSubmitting: boolean = false;
  private online: boolean = true;
  private readonly trySubmitInterval = 1000 * 10; // 10 seconds
  private connectionRestoredSubject = new Subject<never>();

  constructor(
    private connectionStatusService: ConnectionStatusService,
    private offlineStorageTourService: OfflineStorageTourService,
    private immoIPImmoTaskChangesService: ImmoIPImmoTaskChangesService,
    private immoIPImmoTaskSubmitInProgressService: ImmoIPImmoTaskSubmitInProgressService,
    private immoIPImmoTaskSubmitQueueService: ImmoIPImmoTaskSubmitQueueService,
    private tourInstallerSubmitImmoTaskControllerRequestService: TourInstallerSubmitImmoTaskControllerRequestService,
    private tourInstallerSubmitImmoTaskAssetControllerRequestService: TourInstallerSubmitImmoTaskAssetControllerRequestService,
    private tourInstallerDeleteImmoTaskAssetControllerRequestService: TourInstallerDeleteImmoTaskAssetControllerRequestService,
    private tourInstallerResetImmoTaskNotExecutedControllerRequestService: TourInstallerResetImmoTaskNotExecutedControllerRequestService,
    private snackService: SnackService
  ) {
    this.connectionStatusService
      .getConnectionStatusSubject()
      .subscribe((online: boolean) => {
        const doSubmit = !this.online && online;
        this.online = online;
        if (doSubmit) {
          this.connectionRestoredSubject.next();
        }
      });
    merge(
      this.connectionRestoredSubject,
      interval(this.trySubmitInterval)
    ).subscribe(() => {
      this.doSubmit();
    });
  }

  private async doSubmit(): Promise<void> {
    if (this.isSubmitting || !this.online) {
      return;
    }
    this.isSubmitting = true;
    try {
      const toSubmit = await this.immoIPImmoTaskSubmitQueueService.getToSubmit();
      if (!CollectionHelper.isPopulated(toSubmit)) {
        return;
      }
      for (const submitEntry of toSubmit) {
        const immoTaskChanges = await this.immoIPImmoTaskChangesService.getObject(
          submitEntry.immoTaskId
        );
        const isInProgress = await this.immoIPImmoTaskSubmitInProgressService.isInProgress(
          submitEntry.immoTaskId
        );
        if (isInProgress) {
          continue;
        } else if (immoTaskChanges == null) {
          await this.immoIPImmoTaskSubmitQueueService.removeToSubmit(
            submitEntry.immoTaskId
          );
        } else {
          try {
            for (const submitType of submitEntry.submitTypes) {
              if (submitType === ImmoTaskSubmitType.FULL) {
                await this.doSubmitImmoTask(
                  immoTaskChanges,
                  submitEntry.dateString,
                  false
                );
              } else if (submitType === ImmoTaskSubmitType.MODIFY_FEEDBACK) {
                await this.doSubmitImmoTaskChanges(immoTaskChanges, false);
              } else if (submitType === ImmoTaskSubmitType.RESET_NOT_EXECUTED) {
              } else if (
                submitType === ImmoTaskSubmitType.SAVE_EXECUTION_ASSETS
              ) {
                await this.submitOfflineAssets(
                  immoTaskChanges,
                  submitEntry.dateString,
                  false
                );
              } else if (submitType === ImmoTaskSubmitType.DELETE_ASSETS) {
                await this.doDeleteAssetUuids(
                  immoTaskChanges,
                  submitEntry.dateString,
                  false
                );
              }
              await this.immoIPImmoTaskSubmitQueueService.removeTypeFromSubmitEntry(
                immoTaskChanges.id,
                submitType
              );
            }
            await this.immoIPImmoTaskSubmitQueueService.removeToSubmit(
              submitEntry.immoTaskId
            );
          } catch (err) {
            if (!ErrorHelper.isConnectionLostError(err)) {
              this.snackService.showError(
                `Fout bij indienen taak #${submitEntry.immoTaskId}: ${err}`
              );
            }
          }
        }
      }
    } finally {
      this.isSubmitting = false;
    }
  }

  public async retrySubmit(
    immoTaskId: number,
    dateString: string
  ): Promise<void> {
    if (!this.online) {
      this.snackService.showWarning(
        'Er is geen verbinding, gelieve opnieuw te proberen als de verbinding hersteld is'
      );
      return;
    }
    const isInProgress = await this.immoIPImmoTaskSubmitInProgressService.isInProgress(
      immoTaskId
    );
    if (isInProgress) {
      return;
    }
    const immoTaskChanges = await this.immoIPImmoTaskChangesService.getObject(
      immoTaskId
    );
    if (immoTaskChanges == null) {
      await this.immoIPImmoTaskSubmitQueueService.removeToSubmit(immoTaskId);
    } else {
      try {
        await this.doSubmitImmoTask(immoTaskChanges, dateString, false);
      } catch (err) {
        if (!ErrorHelper.isConnectionLostError(err)) {
          this.snackService.showError(
            `Fout bij indienen taak ${immoTaskId}: ${err}`
          );
        }
      }
    }
  }

  public async submitImmoTask(
    immoTaskChanges: ImmoIPImmoTaskChangesObject,
    dateString: string
  ): Promise<void> {
    if (this.online) {
      try {
        await this.doSubmitImmoTask(immoTaskChanges, dateString, true);
      } catch (err) {
        if (ErrorHelper.isConnectionLostError(err)) {
          this.snackService.showWarning(
            'Server kon niet bereikt worden, taak zal ingediend worden zodra internetverbinding hersteld is'
          );
          this.immoIPImmoTaskSubmitQueueService.addToSubmit(
            dateString,
            immoTaskChanges.id,
            ImmoTaskSubmitType.FULL
          );
        }
        throw err;
      }
    } else {
      this.snackService.showWarning(
        'Server kon niet bereikt worden, taak zal ingediend worden zodra internetverbinding hersteld is'
      );
      this.immoIPImmoTaskSubmitQueueService.addToSubmit(
        dateString,
        immoTaskChanges.id,
        ImmoTaskSubmitType.FULL
      );
      return Promise.reject({ status: 0 });
    }
  }

  private async doSubmitImmoTask(
    immoTaskChanges: ImmoIPImmoTaskChangesObject,
    dateString: string,
    immediateSubmit = false
  ): Promise<void> {
    await this.immoIPImmoTaskSubmitInProgressService.setSubmitInProgress(
      immoTaskChanges.id
    );
    try {
      await this.submitOfflineAssets(
        immoTaskChanges,
        dateString,
        immediateSubmit
      );
      const response = await this.tourInstallerSubmitImmoTaskControllerRequestService.submitImmoTask(
        immoTaskChanges
      );
      immoTaskChanges.executedRegistration = response.executedRegistration;
      if (response != null) {
        this.offlineStorageTourService.saveImmoTaskChangeAndEmit(
          dateString,
          immoTaskChanges.id,
          ImmoTaskChangeType.SUBMIT,
          response.executedRegistration,
          ImmoIPPlacerImmoTaskFunctions.setImmoTaskExecutedRegistration
        );
      }
      await this.immoIPImmoTaskSubmitInProgressService.removeSubmitInProgress(
        immoTaskChanges.id
      );
    } catch (err) {
      if (!ErrorHelper.isConnectionLostError(err) && !immediateSubmit) {
        this.snackService.showError(err);
        await this.removeLocalChanges(immoTaskChanges);
      } else {
        if (ErrorHelper.isConnectionLostError(err) && immediateSubmit) {
          this.snackService.showWarning(
            'Server kon niet bereikt worden, taak zal ingediend worden zodra internetverbinding hersteld is'
          );
        } else if (immediateSubmit) {
          this.snackService.showError(err);
        }
      }
      await this.immoIPImmoTaskSubmitInProgressService.removeSubmitInProgress(
        immoTaskChanges.id
      );
      throw err;
    }
  }

  public async submitImmoTaskChanges(
    immoTaskChanges: ImmoIPImmoTaskChangesObject,
    dateString: string
  ): Promise<void> {
    if (this.online) {
      try {
        await this.doSubmitImmoTaskChanges(immoTaskChanges, true);
      } catch (err) {
        if (ErrorHelper.isConnectionLostError(err)) {
          this.snackService.showWarning(
            'Server kon niet bereikt worden, taak zal ingediend worden zodra internetverbinding hersteld is'
          );
          this.immoIPImmoTaskSubmitQueueService.addToSubmit(
            dateString,
            immoTaskChanges.id,
            ImmoTaskSubmitType.MODIFY_FEEDBACK
          );
        }
        throw err;
      }
    } else {
      this.snackService.showWarning(
        'Server kon niet bereikt worden, taak zal ingediend worden zodra internetverbinding hersteld is'
      );
      this.immoIPImmoTaskSubmitQueueService.addToSubmit(
        dateString,
        immoTaskChanges.id,
        ImmoTaskSubmitType.MODIFY_FEEDBACK
      );
      return Promise.reject({ status: 0 });
    }
  }

  private async doSubmitImmoTaskChanges(
    immoTaskChanges: ImmoIPImmoTaskChangesObject,
    immediateSubmit = false
  ): Promise<void> {
    await this.immoIPImmoTaskSubmitInProgressService.setSubmitInProgress(
      immoTaskChanges.id
    );
    try {
      await this.tourInstallerSubmitImmoTaskControllerRequestService.modifyFeedback(
        immoTaskChanges
      );
      await this.immoIPImmoTaskSubmitInProgressService.removeSubmitInProgress(
        immoTaskChanges.id
      );
    } catch (err) {
      if (!ErrorHelper.isConnectionLostError(err) && !immediateSubmit) {
        this.snackService.showError(err);
        await this.removeLocalChanges(immoTaskChanges);
      } else {
        if (ErrorHelper.isConnectionLostError(err) && immediateSubmit) {
          this.snackService.showWarning(
            'Server kon niet bereikt worden, taak zal ingediend worden zodra internetverbinding hersteld is'
          );
        } else if (immediateSubmit) {
          this.snackService.showError(err);
        }
      }
      await this.immoIPImmoTaskSubmitInProgressService.removeSubmitInProgress(
        immoTaskChanges.id
      );
      throw err;
    }
  }

  public async resetNotExecuted(
    immoTaskChanges: ImmoIPImmoTaskChangesObject,
    dateString: string
  ): Promise<void> {
    if (this.online) {
      try {
        await this.doResetNotExecuted(immoTaskChanges, true);
      } catch (err) {
        if (ErrorHelper.isConnectionLostError(err)) {
          this.snackService.showWarning(
            'Server kon niet bereikt worden, taak zal ingediend worden zodra internetverbinding hersteld is'
          );
          this.immoIPImmoTaskSubmitQueueService.addToSubmit(
            dateString,
            immoTaskChanges.id,
            ImmoTaskSubmitType.RESET_NOT_EXECUTED
          );
        }
        throw err;
      }
    } else {
      this.snackService.showWarning(
        'Server kon niet bereikt worden, taak zal ingediend worden zodra internetverbinding hersteld is'
      );
      this.immoIPImmoTaskSubmitQueueService.addToSubmit(
        dateString,
        immoTaskChanges.id,
        ImmoTaskSubmitType.RESET_NOT_EXECUTED
      );
      return Promise.reject({ status: 0 });
    }
  }

  private async doResetNotExecuted(
    immoTaskChanges: ImmoIPImmoTaskChangesObject,
    immediateSubmit = false
  ): Promise<void> {
    await this.immoIPImmoTaskSubmitInProgressService.setSubmitInProgress(
      immoTaskChanges.id
    );
    try {
      await this.tourInstallerResetImmoTaskNotExecutedControllerRequestService.resetNotExecuted(
        immoTaskChanges.id
      );
      await this.immoIPImmoTaskSubmitInProgressService.removeSubmitInProgress(
        immoTaskChanges.id
      );
    } catch (err) {
      if (!ErrorHelper.isConnectionLostError(err) && !immediateSubmit) {
        this.snackService.showError(err);
        await this.removeLocalChanges(immoTaskChanges);
      } else {
        if (ErrorHelper.isConnectionLostError(err) && immediateSubmit) {
          this.snackService.showWarning(
            'Server kon niet bereikt worden, taak zal ingediend worden zodra internetverbinding hersteld is'
          );
        } else if (immediateSubmit) {
          this.snackService.showError(err);
        }
      }
      await this.immoIPImmoTaskSubmitInProgressService.removeSubmitInProgress(
        immoTaskChanges.id
      );
      throw err;
    }
  }

  public async directlySubmitOfflineAssets(
    immoTaskChanges: ImmoIPImmoTaskChangesObject,
    dateString: string,
    immediateSubmit = false
  ): Promise<void> {
    if (this.online) {
      try {
        await this.submitOfflineAssets(
          immoTaskChanges,
          dateString,
          immediateSubmit
        );
      } catch (err) {
        if (ErrorHelper.isConnectionLostError(err)) {
          this.snackService.showWarning(
            'Server kon niet bereikt worden, foto zal ingediend worden zodra internetverbinding hersteld is'
          );
          this.immoIPImmoTaskSubmitQueueService.addToSubmit(
            dateString,
            immoTaskChanges.id,
            ImmoTaskSubmitType.SAVE_EXECUTION_ASSETS
          );
        }
        throw err;
      }
    } else {
      this.snackService.showWarning(
        'Server kon niet bereikt worden, foto zal ingediend worden zodra internetverbinding hersteld is'
      );
      this.immoIPImmoTaskSubmitQueueService.addToSubmit(
        dateString,
        immoTaskChanges.id,
        ImmoTaskSubmitType.SAVE_EXECUTION_ASSETS
      );
      return Promise.reject({ status: 0 });
    }
  }

  private async submitOfflineAssets(
    immoTaskChanges: ImmoIPImmoTaskChangesObject,
    dateString: string,
    immediateSubmit: boolean
  ): Promise<void> {
    if (!CollectionHelper.isPopulated(immoTaskChanges.offlineAssets)) {
      return;
    }
    const unsubmittedAssets = immoTaskChanges.offlineAssets.filter(
      oa => oa.uuid == null
    );
    if (unsubmittedAssets.length > 0) {
      await this.submitMultipleOfflineAssets(
        immoTaskChanges,
        dateString,
        unsubmittedAssets,
        immediateSubmit
      );
    }
  }

  private async submitMultipleOfflineAssets(
    immoTaskChanges: ImmoIPImmoTaskChangesObject,
    dateString: string,
    offlineAssets: Asset[],
    immediateSubmit: boolean
  ): Promise<void> {
    const submitResult = await this.tourInstallerSubmitImmoTaskAssetControllerRequestService.submitMultipleAssets(
      offlineAssets,
      immoTaskChanges.id,
      immediateSubmit
    );
    if (submitResult.resultMap.size > 0) {
      await this.handleAssetSubmitResults(
        dateString,
        submitResult.resultMap,
        immoTaskChanges
      );
    }
    if (submitResult.error != null) {
      if (immediateSubmit) {
        if (ErrorHelper.isConnectionLostError(submitResult.error)) {
          this.snackService.showWarning(
            'Server kon niet bereikt worden, taak zal opnieuw ingediend worden zodra internetverbinding hersteld is'
          );
        } else {
          this.snackService.showError(submitResult.error);
        }
      }
      throw submitResult.error;
    }
  }

  private async handleAssetSubmitResults(
    dateString: string,
    resultMap: Map<string, ImmoTaskAssetSubmitResult>,
    immoTaskChanges: ImmoIPImmoTaskChangesObject
  ): Promise<void> {
    if (CollectionHelper.isPopulated(immoTaskChanges.offlineAssets)) {
      immoTaskChanges.offlineAssets.forEach(oa => {
        const savedAsset =
          oa.uuid == null ? resultMap.get(oa.localFileUuid) : null;
        if (savedAsset != null) {
          oa.uuid = savedAsset.asset.uuid;
        }
      });
    }
    await this.immoIPImmoTaskChangesService.storeObject(immoTaskChanges);
    await this.offlineStorageTourService.saveImmoTaskChangeAndEmit(
      dateString,
      immoTaskChanges.id,
      ImmoTaskChangeType.ASSETS,
      null,
      GeneralHelper.noop
    );
  }

  public async deleteAssets(
    immoTaskChanges: ImmoIPImmoTaskChangesObject,
    dateString: string
  ): Promise<void> {
    if (this.online) {
      try {
        await this.doDeleteAssetUuids(immoTaskChanges, dateString, true);
      } catch (err) {
        if (ErrorHelper.isConnectionLostError(err)) {
          this.snackService.showWarning(
            'Server kon niet bereikt worden, taak zal ingediend worden zodra internetverbinding hersteld is'
          );
          this.immoIPImmoTaskSubmitQueueService.addToSubmit(
            dateString,
            immoTaskChanges.id,
            ImmoTaskSubmitType.DELETE_ASSETS
          );
        }
        throw err;
      }
    } else {
      this.snackService.showWarning(
        'Server kon niet bereikt worden, taak zal ingediend worden zodra internetverbinding hersteld is'
      );
      this.immoIPImmoTaskSubmitQueueService.addToSubmit(
        dateString,
        immoTaskChanges.id,
        ImmoTaskSubmitType.DELETE_ASSETS
      );
      return Promise.reject({ status: 0 });
    }
  }

  private async doDeleteAssetUuids(
    immoTaskChanges: ImmoIPImmoTaskChangesObject,
    dateString: string,
    immediateSubmit = false
  ): Promise<void> {
    if (!CollectionHelper.isPopulated(immoTaskChanges.toDeleteAssetUuids)) {
      return;
    }

    await this.immoIPImmoTaskSubmitInProgressService.setSubmitInProgress(
      immoTaskChanges.id
    );

    try {
      const toDeleteCopy = [...immoTaskChanges.toDeleteAssetUuids];
      await this.tourInstallerDeleteImmoTaskAssetControllerRequestService.deleteAssets(
        toDeleteCopy
      );
      CollectionHelper.diffInPlace(
        immoTaskChanges.toDeleteAssetUuids,
        toDeleteCopy
      );
      await this.immoIPImmoTaskChangesService.storeObject(immoTaskChanges);
      await this.offlineStorageTourService.saveImmoTaskChangeAndEmit(
        dateString,
        immoTaskChanges.id,
        ImmoTaskChangeType.DELETE_ASSETS,
        toDeleteCopy,
        ImmoIPPlacerImmoTaskFunctions.removeDeletedAssets
      );
      await this.immoIPImmoTaskSubmitInProgressService.removeSubmitInProgress(
        immoTaskChanges.id
      );
    } catch (err) {
      if (!ErrorHelper.isConnectionLostError(err) && !immediateSubmit) {
        this.snackService.showError(err);
        await this.removeLocalChanges(immoTaskChanges);
      } else {
        if (ErrorHelper.isConnectionLostError(err) && immediateSubmit) {
          this.snackService.showWarning(
            'Server kon niet bereikt worden, taak zal ingediend worden zodra internetverbinding hersteld is'
          );
        } else if (immediateSubmit) {
          this.snackService.showError(err);
        }
      }
      await this.immoIPImmoTaskSubmitInProgressService.removeSubmitInProgress(
        immoTaskChanges.id
      );
      throw err;
    }
  }

  private async removeLocalChanges(
    immoTaskChanges: ImmoIPImmoTaskChangesObject
  ): Promise<void> {
    await Promise.all([
      this.immoIPImmoTaskSubmitQueueService.removeToSubmit(immoTaskChanges.id),
      this.immoIPImmoTaskChangesService.removeObject(immoTaskChanges)
    ]);
  }
}
